<template>
  <v-container style="max-width: 800px; padding-top: 3rem">
    <div class="text-center mb-8">
      <h1 class="text-h3 font-weight-bold mb-2">Premium</h1>
      <p class="text-body-1 text-grey">
        Elevate your game with AI analysis, online opponents, and more
      </p>
    </div>

    <!-- Interval Toggle -->
    <div class="d-flex justify-center mb-8">
      <v-btn-toggle
        v-model="interval"
        mandatory
        variant="outlined"
        color="primary"
      >
        <v-btn value="monthly">Monthly</v-btn>
        <v-btn value="yearly">
          Yearly
          <v-chip size="x-small" color="success" class="ml-2">Save 17%</v-chip>
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- Pricing Card -->
    <v-card color="surface" class="mx-auto pa-8" max-width="500">
      <div class="text-center mb-6">
        <v-icon color="primary" size="48" class="mb-2">mdi-crown</v-icon>
        <div class="text-h3 font-weight-bold text-primary">
          € {{ displayPrice }}
          <span class="text-h6 text-grey font-weight-regular">
            /{{ interval === "monthly" ? "mth" : "yr" }}
          </span>
        </div>
      </div>

      <v-list density="compact" bg-color="transparent" class="mb-6">
        <v-list-item
          v-for="feature in features"
          :key="feature"
        >
          <template v-slot:prepend>
            <v-icon color="success" size="20">mdi-check-circle</v-icon>
          </template>
          <v-list-item-title class="text-body-2">{{ feature }}</v-list-item-title>
        </v-list-item>
      </v-list>

      <v-divider class="mb-4" />

      <!-- Checkout Buttons -->
      <div class="d-flex flex-column ga-3">
        <v-btn
          color="primary"
          size="large"
          block
          :loading="stripeLoading"
          @click="handleCheckout('stripe')"
        >
          <v-icon start>mdi-credit-card</v-icon>
          Use cards with Stripe
        </v-btn>
        <v-btn
          variant="outlined"
          size="large"
          block
          :loading="paypalLoading"
          @click="handleCheckout('paypal')"
        >
          <v-icon start>mdi-cash</v-icon>
          Pay with PayPal
        </v-btn>
      </div>

      <p
        v-if="!isAuthenticated"
        class="text-caption text-center text-grey mt-4"
      >
        Du skal
        <router-link to="/register" class="text-primary text-decoration-none">
          oprette en konto
        </router-link>
        først.
      </p>
    </v-card>

    <!-- Error Snackbar -->
    <v-snackbar v-model="showError" color="error" timeout="5000">
      {{ errorMessage }}
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../stores/auth";
import { useSubscriptionStore } from "../stores/subscription";

const router = useRouter();
const auth = useAuthStore();
const sub = useSubscriptionStore();

const interval = ref("monthly");
const stripeLoading = ref(false);
const paypalLoading = ref(false);
const showError = ref(false);
const errorMessage = ref("");

const isAuthenticated = computed(() => auth.isAuthenticated);
const plan = computed(() => sub.plan);
const displayPrice = computed(() => {
  if (!plan.value) return "—";
  return interval.value === "monthly"
    ? plan.value.priceMonthly
    : plan.value.priceYearly;
});

const features = [
  "unlimited access to all features",
  "AI-driven game analysis",
  "Find online opponents",
  "Discussion forum",
  "Complete game history and statistics",
  "Priority support",
];

async function handleCheckout(provider) {
  if (!isAuthenticated.value) {
    router.push({ name: "register", query: { redirect: "/pricing" } });
    return;
  }

  provider === "stripe"
    ? (stripeLoading.value = true)
    : (paypalLoading.value = true);

  try {
    await sub.checkout(provider, interval.value);
  } catch (error) {
    errorMessage.value =
      error.response?.data?.error || "Something went wrong. Please try again.";
    showError.value = true;
  } finally {
    stripeLoading.value = false;
    paypalLoading.value = false;
  }
}

onMounted(() => {
  sub.fetchStatus();
});
</script>
